import AdminInterviewCard from '@components/AdminInterviewCard/AdminInterviewCard.component'
import { Box, Button, MenuItem, styled, TextField, Typography, TextareaAutosize, CircularProgress } from '@mui/material'
import { useMutation, useQuery } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
// import { Route as InterviewDurationRoute } from './_admin.interview-duration'
import axios from 'axios'
import { useState } from 'react'
import useMainStore from '../../store/useMainStore'

export const Route = createFileRoute('/(admin)/_admin/admin-interview-from-scratch')({
  component: RouteComponent,
})

const StyledTextArea = styled(TextareaAutosize)({
  backgroundColor: '#F9F9F9',
  width: '100%',
  minHeight: '135px',
  padding: '12px',
  borderRadius: '8px',
  border: '1px solid var(--Interviewer-Lightest-Black)',
  fontFamily: 'inherit',
  fontSize: '14px',
  lineHeight: '20px',
  resize: 'vertical',
  '&:focus': {
    outline: 'none',
  },
})

const StyledInterviewContent = styled(Box)({
  display: 'flex',
  flexDirection: 'row',
  backgroundColor: '#fff',
  padding: '20px',
  border: '1px solid var(--Light-Gray)',
  borderRadius: '12px',
  boxShadow: '0px 1px 2px 0px #0205111A',
})

function RouteComponent() {
  const navigate = useNavigate()
  const setInviteInterviewId = useMainStore.use.setInviteInterviewId()
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [selectedTechnology, setSelectedTechnology] = useState('')
  const [selectedDifficulty, setSelectedDifficulty] = useState('')
  const [interviewCreated, setInterviewCreated] = useState(false)
  const [createdInterviewId, setCreatedInterviewId] = useState<string | null>(null)

  const createInterviewMutation = useMutation({
    mutationFn: async (interviewData: {
      technologyId: string
      difficulty: string
      readTime: string
      answerTime: string
      description: string
      name: string
    }) => {
      const token = localStorage.getItem('token')
      const response = await axios.post(
        'https://interviewer.runasp.net/api/interviews',
        interviewData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        },
      )
      return response.data
    },
    onSuccess: (data) => {
      console.log(data, 'interviewId')
      const interviewId = data
      setInterviewCreated(true)
      setCreatedInterviewId(interviewId)
      setInviteInterviewId(interviewId)
      // navigate({ to: InterviewDurationRoute.to, search: { interviewId } })
    },
    onError: (error) => {
      console.error('Error creating interview:', error)
    },
  })


// Fetch technologies from API
  const { data: technologies, isLoading: isLoadingTechnologies, error: technologiesError } = useQuery({
    queryKey: ['technologies'],
    queryFn: async () => {
      const token = localStorage.getItem('token');
      const response = await axios.get('https://interviewer.runasp.net/api/technologies', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data;
    },
  });

  if (isLoadingTechnologies) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    )
  }

  if (technologiesError) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Typography color="error">Error loading technologies</Typography>
      </Box>
    )
  }

  const categories = [
    {
      id: 'cat-1',
      name: 'Fundamentals',
      description: 'Core concepts and basics of the technology.',
      difficulty: 'entry',
      createdOn: new Date().toISOString(),
      questionCount: 12,
    },
    {
      id: 'cat-2',
      name: 'Advanced Topics',
      description: 'In-depth questions covering advanced topics.',
      difficulty: 'advanced',
      createdOn: new Date().toISOString(),
      questionCount: 18,
    },
  ]

  const interviewContent = {
    categories: [
      {
        id: 'cat-1',
        name: 'Fundamentals',
        questions: [
          { text: 'Explain the component lifecycle.' },
          { text: 'What are hooks and why are they useful?' },
        ],
      },
      {
        id: 'cat-2',
        name: 'Advanced Topics',
        questions: [
          { text: 'How would you optimize rendering performance?' },
          { text: 'Describe a complex state management strategy.' },
        ],
      },
    ],
  }

  const handleCreateInterview = () => {
    if (!selectedTechnology || !selectedDifficulty || !name || !description) {
      alert('Please fill in all required fields')
      return
    }

    const interviewData = {
      technologyId: selectedTechnology,
      difficulty: selectedDifficulty,
      readTime: '200',
      answerTime: '300',
      description: description,
      name: name,
    }

    createInterviewMutation.mutate(interviewData)
  }

  return (
    <>
      <Box>
        <Box
          sx={{
            px: {
              xs: '16px',
              md: '32px',
            },
            py: {
              xs: '16px',
              md: '32px',
            },
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: '20px', md: '48px' }, mb: '64px' }}>
            <TextField
              label="Interview Name"
              variant="outlined"
              fullWidth
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={interviewCreated}
            />
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: '20px' }}>
              <TextField
                label="Select a technology"
                variant="outlined"
                fullWidth
                select
                value={selectedTechnology}
                onChange={(e) => setSelectedTechnology(e.target.value)}
                disabled={interviewCreated}
              >
                {technologies?.map((technology: any) => (
                    <MenuItem key={technology.id} value={technology.id}>
                        {technology.name}
                    </MenuItem>
                ))}
              </TextField>
              <TextField
                label="Select a difficulty"
                variant="outlined"
                fullWidth
                select
                value={selectedDifficulty}
                onChange={(e) => setSelectedDifficulty(e.target.value)}
                disabled={interviewCreated}
              >
                <MenuItem value="entry">Entry</MenuItem>
                <MenuItem value="advanced">Advanced</MenuItem>
                <MenuItem value="professional">Professional</MenuItem>
              </TextField>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <StyledTextArea
                placeholder="Short Interview Description ..."
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                disabled={interviewCreated}
              />
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                sx={{ mt: { xs: '24px', md: '32px' }, minWidth: '180px' }}
                onClick={handleCreateInterview}
                disabled={createInterviewMutation.isPending || interviewCreated}
              >
                {createInterviewMutation.isPending
                  ? 'Creating Interview...'
                  : interviewCreated
                  ? 'Interview Created!'
                  : 'Publish Interview'}
              </Button>
            </Box>
          </Box>

          {/* {interviewCreated && (
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: '20px' }}>
              <Box sx={{ width: '100%' }}>
                <Typography variant="h2" sx={{ pb: '16px' }}>
                  Add a category
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {categories.map((category) => (
                    <AdminInterviewCard key={category.id} category={category} />
                  ))}
                </Box>
              </Box>
              <Box sx={{ width: '100%' }}>
                <Typography variant="h2" sx={{ pb: '16px' }}>
                  Interview Content
                </Typography>
                <StyledInterviewContent>
                  {interviewContent.categories && interviewContent.categories.length > 0 ? (
                    <>
                      <Box sx={{ width: '1px', backgroundColor: 'var(--Interviewer-Near-White)', borderRadius: '1px', mr: '12px' }} />
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px', width: '100%' }}>
                        {interviewContent.categories.map((category: any) => (
                          <Box key={category.id} sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                            <Typography sx={{ fontWeight: '700', fontSize: '16px', color: 'var(--Interviewer-Black)' }}>
                              {category.name}
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px', pl: '20px' }}>
                              {category.questions && category.questions.length > 0 ? (
                                category.questions.map((question: any, index: number) => (
                                  <Typography key={index} sx={{ fontWeight: '700', color: 'var(--Interviewer-Black)' }}>
                                    {question.text || `Question ${index + 1}`}
                                  </Typography>
                                ))
                              ) : (
                                <Typography sx={{ fontWeight: '400', color: 'var(--Interviewer-Black)', fontStyle: 'italic' }}>
                                  No questions available for this category
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    </>
                  ) : (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', py: 4 }}>
                      <Typography color="text.secondary">No content available for this interview</Typography>
                    </Box>
                  )}
                </StyledInterviewContent>
              </Box>
            </Box>
          )} */}
        </Box>
      </Box>
    </>
  )
}
